/*
  File: RolesContext.tsx
  Author: Binish Mookken
  Date: April-June 2024

  Description:
  This file defines the RolesContext and RolesProvider components for managing roles data within the application.
  It provides a context for sharing roles data across components and includes a custom hook (useRolesData) to
  consume the roles data.
*/

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { getRoles } from "../service/service";
import { Role } from "../types";

interface RolesContextType {
  rolesData: Role[];
}

const RolesContext = createContext<RolesContextType>({ rolesData: [] });

export const RolesProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [rolesData, setRolesData] = useState<Role[]>([]);

  useEffect(() => {
    const fetchRolesData = async () => {
      try {
        const response = await getRoles();
        setRolesData(response);
      } catch (error) {
        console.error("Error fetching roles data:", error);
      }
    };

    fetchRolesData();
  }, []);

  const contextValue: RolesContextType = {
    rolesData,
  };

  return (
    <RolesContext.Provider value={contextValue}>
      {children}
    </RolesContext.Provider>
  );
};

export const useRolesData = () => useContext(RolesContext);
